import { useContext, useState, useEffect, useCallback } from 'react';
import type React from 'react';
import { useIsMobile } from '~/contexts/DeviceContext';
import { useTradeInfo } from '~/contexts/FinanceAndTradeContext';
import SearchContext from '~/contexts/SearchContext';
import { useHasActiveTradeApplied } from '~/hooks/selectors';
import { FlexWrapper, Panel } from './BudgetFinance.styles';
import CashDownDescriptors from './CashDownDescriptors';
import FinancingCard from './FinancingCard';
import BudgetContext from '../BudgetContext';
import FacetFinanceRange, {
  type FacetValue,
  InputType
} from '../Ranges/FacetFinanceRange';
import TradeCard from '../TradeCard';

export default function BudgetFinance() {
  const budgetContext = useContext(BudgetContext);
  const { showFinance } = useContext(SearchContext);
  const isMobile = useIsMobile();

  // Selector states
  const hasActiveTradeApplied = useHasActiveTradeApplied();
  const tradeInfo = useTradeInfo();

  // States
  const [inputUpdated, setInputUpdated] = useState<FacetValue | undefined>();
  const [isSliderFocused, setIsSliderFocused] = useState(false);

  useEffect(() => {
    if (inputUpdated == null) return;

    budgetContext.onChange.call(null);

    setInputUpdated(undefined);
  }, [budgetContext.onChange, inputUpdated]);

  const handleChange = useCallback(
    (type: FacetValue, rangeValue: number, eventType: InputType) => {
      const shouldUpdate = eventType === 'input' || isSliderFocused || isMobile;

      if (shouldUpdate) {
        const sliderUpdateMethods = {
          downPayment: budgetContext.setCurrentDownPayment,
          monthlyPayment: budgetContext.setCurrentMonthlyPayment
        };

        const sliderUpdateMethod = sliderUpdateMethods[type];
        if (sliderUpdateMethod) sliderUpdateMethod(rangeValue);
      }
    },
    [
      budgetContext.setCurrentDownPayment,
      budgetContext.setCurrentMonthlyPayment,
      isMobile,
      isSliderFocused
    ]
  );

  const handleInputChange = useCallback(
    (type: FacetValue, ev: React.BaseSyntheticEvent) => {
      const value = parseInt(ev.target.value.replace(/\D/g), 10);
      let isValidValue = !Number.isNaN(value);
      if (type === 'downPayment') {
        isValidValue = isValidValue && value >= budgetContext.minDownPayment;
      }

      if (type === 'monthlyPayment') {
        isValidValue = isValidValue && value <= budgetContext.maxMonthlyPayment;
      }

      if (isValidValue) {
        handleChange(type, value, 'input');
        setInputUpdated(type);
      }
    },
    [
      budgetContext.maxMonthlyPayment,
      budgetContext.minDownPayment,
      handleChange
    ]
  );

  const debounceDelay = isMobile ? 300 : 2500;

  const handleSliderFocus = useCallback(() => {
    setIsSliderFocused(true);
  }, []);

  const handleSliderBlur = useCallback(() => {
    setIsSliderFocused(false);
  }, []);

  return (
    <>
      <FlexWrapper>
        <Panel flex={1}>
          <FacetFinanceRange
            debounceDelay={debounceDelay}
            facetValue="downPayment"
            inputId="downPaymentInput"
            inputWidth={64}
            minRange={budgetContext.minDownPayment}
            maxRange={budgetContext.minDownPayment + 10000}
            onAfterChange={budgetContext.onChange}
            onChange={handleChange}
            onInputChange={handleInputChange}
            step={500}
            title="Cash Down"
            isMobile={isMobile}
            inputValue={budgetContext.currentDownPayment}
            sliderValue={
              budgetContext.currentDownPayment ?? budgetContext.minDownPayment
            }
            handleSliderFocus={handleSliderFocus}
            handleSliderBlur={handleSliderBlur}
          />
          <CashDownDescriptors
            minDownPaymentNoTrade={budgetContext.minDownPaymentNoTrade}
            hasActiveTradeApplied={hasActiveTradeApplied}
            minDownPayment={budgetContext.minDownPayment}
            tradeDown={tradeInfo?.totalTradeInEquity}
          />
        </Panel>
        <Panel flex={1}>
          <FacetFinanceRange
            debounceDelay={debounceDelay}
            facetValue="monthlyPayment"
            inputId="monthlyPaymentInput"
            inputWidth={64}
            onAfterChange={budgetContext.onChange}
            onChange={handleChange}
            onInputChange={handleInputChange}
            minRange={0}
            maxRange={budgetContext.maxMonthlyPayment}
            step={5}
            title="Monthly Payment"
            isMobile={isMobile}
            inputValue={
              budgetContext.currentMonthlyPayment ??
              budgetContext.maxMonthlyPayment
            }
            sliderValue={
              budgetContext.currentMonthlyPayment ??
              budgetContext.maxMonthlyPayment
            }
            withLessThan
            handleSliderFocus={handleSliderFocus}
            handleSliderBlur={handleSliderBlur}
          />
        </Panel>
      </FlexWrapper>
      <FlexWrapper>
        {showFinance && (
          <Panel flex={1}>
            <FinancingCard />
          </Panel>
        )}
        <Panel flex={1}>
          <TradeCard />
        </Panel>
      </FlexWrapper>
    </>
  );
}
